import React from 'react';

import InfoIcon from '@mui/icons-material/Info';
import { useDispatch, useSelector } from 'react-redux';

import { salesPhotosSelector } from '../selector';
import { refreshSalesPhotoList } from '../thunk';

import GroupDetail from './GroupDetail';

import type { TableBaseColumnType, TableBaseRowType } from '@/components/TableBase/Body';
import type { EntityDataPubNotNullType } from '@/domain/entityPub';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { PhotoSalesJsonPub, PhotoSalesJsonPvt } from '@/types/domain/photo/entities/photoSalesEntity';

import TableBase from '@/components/TableBase';
import { convertDateToDisplayString } from '@/utils/date';

/**
 * プロパティ
 */
export type Props = {
  /** 子ども */
  child: ChildJson,
}

const GroupList: React.FC<Props> = ({
  child,
}) => {
  const dispatch = useDispatch();
  const salesPhotos = useSelector(salesPhotosSelector);
  const [salesDetail, setSalesDetail] = React.useState<
    EntityDataPubNotNullType<PhotoSalesJsonPvt, PhotoSalesJsonPub
    > | null>(null);
  React.useEffect(() => {
    dispatch(refreshSalesPhotoList({ parentChildId: `${child.parentId}_${child.childId}` }));
  }, [dispatch, child]);
  const rows = salesPhotos.filter((v) => {
    const d = new Date(v.data.expirationDate);
    d.setDate(d.getDate() + (v.data.extensionDays || 0));
    d.setHours(23);
    d.setMinutes(59);
    d.setSeconds(59);
    return d >= new Date();
  }).map((v) => {
    const expirationDate = v.data.extensionDays
      ? `${convertDateToDisplayString(v.data.expirationDate.split('T')[0])} +${v.data.extensionDays}日`
      : convertDateToDisplayString(v.data.expirationDate.split('T')[0]);
    return {
      id: v.data.salesGroupId,
      title: v.data.salesGroupName,
      expirationDate,
      createDatetime: v.data.lastModifiedDatetime,
    };
  }).sort((a, b) => (
    (new Date(a.createDatetime)).getTime() < (new Date(b.createDatetime)).getTime() ? 1 : -1));
  // こどもがふたりいる場合の重複回避
  const uniqueRows = Array.from(
    new Map(rows.map((row) => [row.id, row])).values(),
  );
  const columns: TableBaseColumnType[] = [
    { id: 'title', label: 'タイトル', minWidth: 100 },
    { id: 'expirationDate', label: '公開期限', minWidth: 100 },
  ];
  const actions = [
    {
      icon: <InfoIcon />,
      title: '購入・詳細表示',
      action: async (r: TableBaseRowType) => {
        const item = salesPhotos.find((v) => v.data.salesGroupId === r.id);
        if (item) {
          setSalesDetail(item);
        }
      },
    },
  ];
  if (uniqueRows.length === 0) {
    return (
      <p>写真販売はありません。</p>
    );
  }
  return (
    <div>
      <TableBase
        rows={uniqueRows}
        columns={columns}
        actions={actions}
        isPagination={false}
        isShowFilter={false}
        isShowFooter={false}
      />
      {salesDetail && (
        <GroupDetail
          close={() => setSalesDetail(null)}
          sales={salesDetail}
        />
      )}
    </div>
  );
};

export default GroupList;
